import React from 'react'
import { Box } from '@mui/material'
import { EmtButton, EmtIcon, useMobile } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import SelectMoreModal from '@/components/pages/pet-sitter/reservations/NewRequest/SelectMoreModal'

import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import useCurrentUser from '@/hooks/useCurrentUser'

interface PetsittersSelectionButtonsProps {
  id: string
  selectClickOverride?: () => void
  compact?: boolean
  sx?: any
}

const PetsittersSelectionButtons: React.FC<PetsittersSelectionButtonsProps> = ({
  id,
  selectClickOverride,
  compact,
  sx,
}) => {
  const { t } = useTranslation('petsitter')
  const [isMobile] = useMobile()
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const addPetsitter = useNewRequestCartStore((s) => s.addPetsitter)
  const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)
  const addPetsitterDisabled = petsittersIds.length >= 10
  const [openModal, setOpenModal] = React.useState(false)

  const select = !petsittersIds.includes(id)
  const remove = petsittersIds.includes(id)

  const handleAddPetsitter = async () => {
    if (selectClickOverride) {
      selectClickOverride()
      return
    } else if (id) {
      await addPetsitter(id)
      // Send gtm event when a pet sitter is added to the selection
      gtmSendCustomEvent('member_add_to_selection_sst', {
        member_role_variable: 'maitre',
        member_id_variable: currentUser?.id || 'unknown',
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
      })
    }
    if (petsittersIds.length === 0) {
      setOpenModal(true)
    } else if (isMobile) {
      router.back()
    }
  }

  return (
    <Box>
      {select && (
        <EmtButton
          onClick={() => handleAddPetsitter()}
          fullWidth
          color="purple"
          disabled={addPetsitterDisabled}
          sx={{
            ...(compact && {
              minWidth: 'unset',
              minHeight: 'unset',
              width: '100%',
              height: '48px',
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }),
            ...sx,
          }}
          startIcon={
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '50%',
                width: '14px',
                height: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EmtIcon
                className="emt-add-small"
                sx={{
                  fontSize: '17px',
                  color: '#AE59C6',
                }}
              />
            </Box>
          }
        >
          {compact ? '' : t('petSitterSelection.addToSelection')}
        </EmtButton>
      )}
      {remove && (
        <EmtButton
          fullWidth
          onClick={() => removePetsitter(id)}
          color="purple"
          variant="outlined"
          sx={{
            background: 'white',
            '&:hover': { background: 'white' },
            ...(compact && {
              minWidth: 'unset',
              minHeight: 'unset',
              width: '100%',
              height: '48px',
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }),
            ...sx,
          }}
          startIcon={
            <Box
              sx={{
                backgroundColor: '#AE59C6',
                borderRadius: '50%',
                width: '14px',
                height: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #fff',
              }}
            >
              <EmtIcon
                className="emt-minus-small"
                sx={{
                  fontSize: '17px',
                  color: '#fff',
                }}
              />
            </Box>
          }
        >
          {compact ? '' : t('petSitterSelection.removeFromSelection')}
        </EmtButton>
      )}
      <SelectMoreModal open={openModal} setOpen={setOpenModal} id={id} />
    </Box>
  )
}

export default PetsittersSelectionButtons
